import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, Loader } from 'lucide-react';

const PullToRefresh = ({ onRefresh, children, disabled = false }) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isPulling, setIsPulling] = useState(false);

    const startY = useRef(0);
    const containerRef = useRef(null);

    const PULL_THRESHOLD = 80;
    const MAX_PULL = 150;

    const isAtTop = () => {
        return window.scrollY === 0 || document.documentElement.scrollTop === 0;
    };

    // Utiliser useEffect pour ajouter les listeners avec { passive: false }
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleTouchStart = (e) => {
            if (disabled || isRefreshing || !isAtTop()) return;
            startY.current = e.touches[0].clientY;
            setIsPulling(true);
        };

        const handleTouchMove = (e) => {
            if (disabled || !isPulling || isRefreshing || !isAtTop()) return;

            const currentY = e.touches[0].clientY;
            const distance = currentY - startY.current;

            if (distance > 0) {
                // CORRECTION : preventDefault dès le début
                e.preventDefault();

                const dampedDistance = Math.min(distance * 0.5, MAX_PULL);
                setPullDistance(dampedDistance);
            }
        };

        const handleTouchEnd = async () => {
            if (!isPulling || isRefreshing) return;
            setIsPulling(false);

            if (pullDistance >= PULL_THRESHOLD) {
                setIsRefreshing(true);
                console.log('🔄 Pull-to-refresh déclenché');

                try {
                    await onRefresh();
                    console.log('✅ Refresh terminé');
                } catch (error) {
                    console.error('❌ Erreur refresh:', error);
                } finally {
                    setTimeout(() => {
                        setIsRefreshing(false);
                        setPullDistance(0);
                    }, 500);
                }
            } else {
                setPullDistance(0);
            }
        };

        // CORRECTION : passive: false pour permettre preventDefault
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: false });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isPulling, isRefreshing, pullDistance, onRefresh]);

    useEffect(() => {
        return () => {
            setIsPulling(false);
            setPullDistance(0);
            setIsRefreshing(false);
        };
    }, []);

    const getOpacity = () => {
        return Math.min(pullDistance / PULL_THRESHOLD, 1);
    };

    return (
        <div
            ref={containerRef}
            className="relative min-h-screen"
        >
            {/* Indicateur de pull-to-refresh */}
            <div
                className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center pointer-events-none transition-all duration-300"
                style={{
                    height: `${pullDistance}px`,
                    opacity: getOpacity(),
                }}
            >
                <div
                    className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-full p-3 shadow-2xl transition-all duration-300"
                    style={{
                        transform: `scale(${Math.min(pullDistance / PULL_THRESHOLD, 1)})`,
                    }}
                >
                    {isRefreshing ? (
                        <Loader className="w-6 h-6 text-white animate-spin" />
                    ) : (
                        <RefreshCw
                            className="w-6 h-6 text-white transition-transform duration-200"
                            style={{
                                transform: `rotate(${(pullDistance / PULL_THRESHOLD) * 360}deg)`,
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Message */}
            {pullDistance > 20 && (
                <div
                    className="fixed top-16 left-0 right-0 z-[60] text-center pointer-events-none transition-all duration-300"
                    style={{
                        opacity: getOpacity(),
                    }}
                >
                    <p className="text-white font-semibold text-sm bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 inline-block">
                        {isRefreshing
                            ? 'Actualisation...'
                            : pullDistance >= PULL_THRESHOLD
                                ? 'Relâchez pour actualiser'
                                : 'Tirez pour actualiser'}
                    </p>
                </div>
            )}

            {/* Contenu */}
            {children}
        </div>
    );
};

export default PullToRefresh;